import {
  extractFileNameFromUrl,
  resolveSimulatorAttachmentKind,
} from "./agentSimulatorAttachmentKind";
import { parseChatbotInteractiveButtons } from "./chatbotQuickReplies";

/**
 * Payload enviado ao motor ao clicar num quick-reply no simulador.
 * Mesmo contrato dos canais WhatsApp (Meta / Gupshup / API Fácil):
 * `resolveInteractiveInboundChoice` — id/postback numérico prevalece sobre rótulo
 * genérico "Selecionar"; botões só com label (Somente ida, CONFIRMAR) seguem o texto.
 *
 * @param {string} label
 * @param {string} [optionId]
 * @returns {string}
 */
export function resolveSimulatorQuickReplyPayload(label, optionId = "") {
  const id = String(optionId || "").trim();
  const title = String(label || "").trim();
  if (/^\d{1,3}$/.test(id)) return id;
  return id || title;
}

function attachmentBubble(att, agentName, replyText = "") {
  const url = String(att.fileUrl || att.url || "").trim();
  if (!url) return null;
  const contentType = String(att.contentType || "").toLowerCase();
  const fileName =
    String(att.fileName || att.name || extractFileNameFromUrl(url) || "").trim() || "Anexo";
  const messageType = att.messageType || null;
  const kind = resolveSimulatorAttachmentKind({
    messageType,
    contentType,
    url,
    fileName,
  });
  const rawCaption = String(att.content || att.caption || "").trim();
  const caption =
    rawCaption && rawCaption !== fileName && !replyText.includes(rawCaption) ? rawCaption : "";
  return {
    from: "bot",
    agentName,
    text: caption || "",
    attachment: { kind, url, fileName, contentType, messageType },
  };
}

function segmentToBotMessage(seg, agentName) {
  const text = String(seg.text ?? "").trim();
  const { replies, links, text: enrichedText } = parseChatbotInteractiveButtons({
    ...seg,
    text,
  });
  const buttons = replies.map((entry) => entry.label);
  const buttonOptions = replies.map((entry) => ({
    label: entry.label,
    id: String(entry.id || "").trim(),
  }));
  const linkButtons = links;
  const displayText = links.length && enrichedText != null ? String(enrichedText).trim() : text;

  const attachmentList = [];
  if (seg.attachment) attachmentList.push(seg.attachment);
  if (Array.isArray(seg.attachments)) attachmentList.push(...seg.attachments);
  if (Array.isArray(seg.arquivos)) attachmentList.push(...seg.arquivos);

  const rawCaptions = attachmentList
    .map((att) => String(att?.content || att?.caption || att?.legenda || "").trim())
    .filter(Boolean);
  const textIsAttachmentCaption = Boolean(displayText && rawCaptions.includes(displayText));

  const attachmentBubbles = attachmentList
    // Se o texto do segmento é só a legenda do anexo, não suprima a legenda na imagem.
    .map((att) => attachmentBubble(att, agentName, textIsAttachmentCaption ? "" : displayText))
    .filter(Boolean);

  const messages = [];
  if ((displayText || buttons.length || linkButtons.length) && !textIsAttachmentCaption) {
    messages.push({ from: "bot", text: displayText, buttons, buttonOptions, linkButtons, agentName });
  } else if (!displayText && (buttons.length || linkButtons.length)) {
    messages.push({ from: "bot", text: "", buttons, buttonOptions, linkButtons, agentName });
  }

  messages.push(...attachmentBubbles);
  return messages;
}

/** Converte resposta da API de simulação do agente em mensagens do simulador. */
export function botMessagesFromAgentResponse(dataMsg) {
  if (!dataMsg || typeof dataMsg !== "object") return [];

  const agentName = dataMsg.agentName ? String(dataMsg.agentName).trim() : "";
  const segments = Array.isArray(dataMsg.mensagens) ? dataMsg.mensagens : [];

  if (segments.length) {
    return segments.flatMap((seg) => segmentToBotMessage(seg, agentName)).filter(Boolean);
  }

  const messages = [];
  const reply = String(dataMsg.reply || "").trim();
  if (reply) {
    const { replies, links, text: enrichedText } = parseChatbotInteractiveButtons({
      ...dataMsg,
      text: reply,
    });
    const displayText = links.length && enrichedText != null ? String(enrichedText).trim() : reply;
    messages.push({
      from: "bot",
      text: displayText,
      agentName,
      buttons: replies.map((e) => e.label),
      buttonOptions: replies.map((e) => ({
        label: e.label,
        id: String(e.id || "").trim(),
      })),
      linkButtons: links,
    });
  }

  const attachments = Array.isArray(dataMsg.attachments) ? dataMsg.attachments : [];
  for (const att of attachments) {
    const bubble = attachmentBubble(att, agentName, reply);
    if (bubble) messages.push(bubble);
  }

  return messages;
}

export function buildAgentSimulatorTurnMessages(dataMsg, userText) {
  const trimmed = String(userText || "").trim();
  const userMessage = trimmed ? { from: "user", text: trimmed } : null;
  const botMessages = botMessagesFromAgentResponse(dataMsg);
  return userMessage ? [userMessage, ...botMessages] : botMessages;
}

const TRANSFER_FALLBACK = {
  humano: (label) => `Solicitação transferida para ${label}.`,
  fila_humano: () => "Solicitação transferida para a fila de atendimento humano.",
  agente_ia: (label) => `Solicitação transferida para o agente ${label}.`,
  chatbot: (label) => `Solicitação transferida para o chatbot ${label}.`,
  generic: () => "Solicitação transferida.",
  hint: "Este teste encerrou. Clique em Limpar para começar outro.",
};

function translateOrFallback(t, key, fallback, vars) {
  if (typeof t !== "function") return fallback;
  const out = t(key, vars);
  return out && out !== key ? out : fallback;
}

/** Aviso de sistema quando o lead sai do agente testado (humano, fila, outro agente ou chatbot). */
export function buildSimulationTransferSystemMessage(dataMsg, t) {
  const info = dataMsg?.transfer && typeof dataMsg.transfer === "object" ? dataMsg.transfer : null;
  const transferred = dataMsg?.transferred === true || info?.transferred === true;
  if (!transferred) return null;

  const dest = String(info?.dest || "").trim();
  const label = String(info?.label || "").trim();
  let headline = translateOrFallback(
    t,
    "agentsListPage.simulatorTransferredGeneric",
    TRANSFER_FALLBACK.generic()
  );
  if (dest === "humano") {
    const name = label || "atendente";
    headline = translateOrFallback(
      t,
      "agentsListPage.simulatorTransferredHuman",
      TRANSFER_FALLBACK.humano(name),
      { label: name }
    );
  } else if (dest === "fila_humano") {
    headline = translateOrFallback(
      t,
      "agentsListPage.simulatorTransferredQueue",
      TRANSFER_FALLBACK.fila_humano()
    );
  } else if (dest === "agente_ia") {
    const name = label || "outro agente de IA";
    headline = translateOrFallback(
      t,
      "agentsListPage.simulatorTransferredAgent",
      TRANSFER_FALLBACK.agente_ia(name),
      { label: name }
    );
  } else if (dest === "chatbot") {
    const name = label || "chatbot";
    headline = translateOrFallback(
      t,
      "agentsListPage.simulatorTransferredChatbot",
      TRANSFER_FALLBACK.chatbot(name),
      { label: name }
    );
  }

  const hint = translateOrFallback(
    t,
    "agentsListPage.simulatorTransferredHint",
    TRANSFER_FALLBACK.hint
  );
  return { from: "system", kind: "transfer", text: `${headline}\n${hint}` };
}

const SKIP_FALLBACK = {
  agent_inactive:
    "Este agente está inativo, por isso não responde no teste nem nas solicitações reais.\nAtive o interruptor «Agente de IA ativo?» no topo desta tela, salve o agente e teste de novo.",
  agent_not_found:
    "Não encontrei este agente. Recarregue a página ou volte à lista de agentes e abra o cadastro novamente.",
  missing_agent_id:
    "Faltou o identificador do agente neste teste. Recarregue a página e tente de novo.",
  invalid_conversation:
    "A sessão de teste ficou inválida. Clique em Limpar e envie a mensagem outra vez.",
  missing_company:
    "Não foi possível identificar o workspace deste teste. Recarregue a página e tente de novo.",
  duplicate_inbound:
    "Esta mensagem já estava sendo processada. Aguarde um instante ou clique em Limpar para testar de novo.",
  empty_reply:
    "O agente não gerou resposta neste turno. Confira se ele está ativo, se a função está configurada e se as instruções de IA foram salvas.",
  no_engine_result:
    "O teste não chegou a executar o agente. Clique em Limpar e tente de novo. Se continuar, recarregue a página.",
};

const SKIP_I18N_KEYS = {
  agent_inactive: "agentsListPage.simulatorSkipInactive",
  agent_not_found: "agentsListPage.simulatorSkipNotFound",
  missing_agent_id: "agentsListPage.simulatorSkipMissingId",
  invalid_conversation: "agentsListPage.simulatorSkipInvalidSession",
  missing_company: "agentsListPage.simulatorSkipMissingCompany",
  duplicate_inbound: "agentsListPage.simulatorSkipDuplicate",
  empty_reply: "agentsListPage.simulatorSkipEmptyReply",
  no_engine_result: "agentsListPage.simulatorSkipNoEngine",
};

/** Aviso de sistema quando o agente não responde (inativo, vazio, etc.). */
export function buildSimulationSkipSystemMessage(dataMsg, t) {
  const notice = dataMsg?.notice && typeof dataMsg.notice === "object" ? dataMsg.notice : null;
  const code = String(notice?.code || dataMsg?.skipReason || "").trim();
  if (!code && !notice?.text) return null;

  const fallback = SKIP_FALLBACK[code] || notice?.text || SKIP_FALLBACK.empty_reply;
  const key = SKIP_I18N_KEYS[code];
  const text = key
    ? translateOrFallback(t, key, fallback)
    : String(notice?.text || fallback).trim();
  if (!text) return null;
  return { from: "system", kind: "skip", text };
}
